import React, { createContext, useContext, useState, useEffect } from 'react'
import { authAPI } from '../services/api'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  // Check for existing session on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('super_admin_token')
      const userData = localStorage.getItem('super_admin_user')
      
      if (token && userData) {
        try {
          // Parse stored user data first
          const parsedUser = JSON.parse(userData)
          
          // Verify user is super_admin
          if (parsedUser.role !== 'super_admin') {
            throw new Error('Insufficient privileges')
          }
          
          // Set user immediately from localStorage
          setIsAuthenticated(true)
          setUser(parsedUser)
          
          // Validate token in background
          try {
            const response = await authAPI.getProfile()
            if (!response.success || response.data.role !== 'super_admin') {
              throw new Error('Invalid token or insufficient privileges')
            }
          } catch (validationError) {
            console.warn('Token validation failed:', validationError.message)
            // Clear invalid session
            localStorage.removeItem('super_admin_token')
            localStorage.removeItem('super_admin_user')
            setIsAuthenticated(false)
            setUser(null)
          }
        } catch (error) {
          console.error('Auth data parsing error:', error.message)
          localStorage.removeItem('super_admin_token')
          localStorage.removeItem('super_admin_user')
          setIsAuthenticated(false)
          setUser(null)
        }
      }
      setLoading(false)
    }

    checkAuthStatus()
  }, [])

  const login = async (username, password) => {
    try {
      const response = await authAPI.login(username, password)

      if (response.success) {
        const { user, token } = response.data || response
        
        // Verify user is super_admin
        if (user.role !== 'super_admin') {
          return { 
            success: false, 
            error: 'Access denied. Super admin privileges required.' 
          }
        }
        
        localStorage.setItem('super_admin_token', token)
        localStorage.setItem('super_admin_user', JSON.stringify(user))
        
        setIsAuthenticated(true)
        setUser(user)
        
        return { success: true }
      } else {
        return { success: false, error: response.message || 'Login failed' }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed'
      }
    }
  }

  const logout = async () => {
    try {
      await authAPI.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('super_admin_token')
      localStorage.removeItem('super_admin_user')
      setIsAuthenticated(false)
      setUser(null)
    }
  }

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    loading
  }

  if (loading) {
    return (
      <div className="loading-spinner">
        <div>Loading Super Admin Panel...</div>
      </div>
    )
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}